/** @jsxImportSource @emotion/react */
import { FORM_ERROR } from 'final-form';
import type { AdminEmailReportConfiguration } from '../../../../../types/EmailReportSubscription';
import AlertBox from '../../../components/AlertBox';
import { Form } from '../../../components/Form';
import { FormErrorMessage } from '../../../components/Form/FormErrorMessage';
import {
    FormButtonContainer,
    FormCheckbox,
    FormCheckboxContainer,
    FormRichTextArea,
    FormSelect,
} from '../../../components/Form/FormInputs';
import { FormItem } from '../../../components/Form/FormItem';
import { FormLabel } from '../../../components/Form/FormLabel';
import SaveButton, { DISABLED, ENABLED, SAVING } from '../../../components/SaveButton';
import r from '../../../services/Replacement';
interface Props {
    onSubmit: (subscription: AdminEmailReportConfiguration) => Promise<void>;
}

type Frequency = 1 | 2;
const REPORT_FREQ_OPTIONS: Frequency[] = [1, 2];

const frequencyToOptionName = (f: Frequency) => {
    if (f === 1) {
        return 'once per week';
    } else {
        return 'once every two weeks';
    }
};

export default function AdminReportSubscriptionForm({ onSubmit }: Props) {
    const handleSubmit = (form: AdminEmailReportConfiguration) =>
        onSubmit(form).catch(() => ({
            [FORM_ERROR]: 'We were unable to subscribe you to this report. Please reload and try again.',
        }));

    const showDisclaimer = (form: AdminEmailReportConfiguration) => {
        return form.feedback || form.meetings || form.objectives;
    };

    return (
        <Form<AdminEmailReportConfiguration>
            onSubmit={handleSubmit}
            initialValues={{
                feedback: false,
                objectives: false,
                reviews: true,
                meetings: false,
                reportFrequencyWeeks: 1,
                otherInfo: undefined,
            }}
        >
            {({ submitting, invalid, submitError, values }) => (
                <div>
                    <FormLabel>Which features would you like to receive reporting about?</FormLabel>
                    <FormCheckboxContainer>
                        <FormItem
                            name="reviews"
                            type="checkbox"
                            label={r('pr.menu.title')}
                            inputComponent={FormCheckbox}
                        />
                        <FormItem
                            name="feedback"
                            type="checkbox"
                            label={r('fr.menu.title')}
                            inputComponent={FormCheckbox}
                        />
                        <FormItem
                            name="objectives"
                            type="checkbox"
                            label={r('objective.menu.title')}
                            inputComponent={FormCheckbox}
                        />
                        <FormItem
                            name="meetings"
                            type="checkbox"
                            label={r('meeting.name.plural')}
                            inputComponent={FormCheckbox}
                        />
                        <div css={{ visibility: showDisclaimer(values) ? 'visible' : 'hidden' }}>
                            <AlertBox colour="YELLOW">
                                Disclaimer: in the early stage of this feature, it’s only possible to provide you with
                                reports on {r('pr.menu.title')}. Your feedback will help us expand this feature in the
                                future.
                            </AlertBox>
                        </div>
                    </FormCheckboxContainer>
                    <FormLabel>How frequently would you like to receive these reports?</FormLabel>
                    <FormItem
                        name="reportFrequencyWeeks"
                        label=""
                        inputComponent={FormSelect}
                        inputProps={{
                            optionToText: frequencyToOptionName,
                            options: REPORT_FREQ_OPTIONS,
                        }}
                    />
                    <FormLabel>
                        Would you or your team like to receive reports on other topics? If you have more details to
                        share about how a specific report would help, we’d love to hear more.
                    </FormLabel>
                    <FormItem
                        name="otherInfo"
                        label=""
                        subLabel="(optional)"
                        inputComponent={FormRichTextArea}
                        inputProps={{
                            hideToolbar: true,
                            rows: 3,
                            disabled: submitting,
                            onSubmitThroughKeyboard: handleSubmit,
                        }}
                    />
                    <FormButtonContainer>
                        <SaveButton
                            state={submitting ? SAVING : ENABLED}
                            labels={{
                                [ENABLED]: `Subscribe to reports`,
                                [DISABLED]: `Subscribe to reports`,
                                [SAVING]: `Subscribing`,
                            }}
                            disabled={invalid}
                        />
                        {submitError ? <FormErrorMessage errorMessage={submitError} /> : null}
                    </FormButtonContainer>
                </div>
            )}
        </Form>
    );
}
